import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Monitor, 
  Users, 
  Trophy, 
  Clock, 
  Target,
  X,
  Check,
  Minus,
  RotateCcw,
  ArrowLeftRight,
  Download,
  Share2,
  Image
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe clipboard copy with fallback
const copyToClipboard = (text) => {
  // Try using a textarea fallback first (works in more contexts)
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    toast.success('Copied to clipboard!');
    return true;
  } catch (err) {
    // If that fails, show a prompt
    window.prompt('Copy this URL:', text);
    return false;
  }
};

const GraphicsPanel = ({ 
  game, 
  gameId,
  getAuthHeaders,
  onGameUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [generatingGraphic, setGeneratingGraphic] = useState(null);
  const [penaltyPlayer, setPenaltyPlayer] = useState({ team1: '', team2: '' });
  const [substitution, setSubstitution] = useState({
    team: '1',
    playerOut: '',
    playerOutNumber: '',
    playerIn: '',
    playerInNumber: ''
  });

  const generateSocialGraphic = async (teamNumber, graphicType) => {
    const key = `${teamNumber}_${graphicType}`;
    setGeneratingGraphic(key);
    try {
      const response = await axios.post(`${API}/graphics/generate`, {
        game_id: gameId,
        team_number: teamNumber,
        graphic_type: graphicType
      }, {
        headers: getAuthHeaders()
      });
      
      // Download the generated graphic using fetch + blob
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      // Fetch the image as blob
      const imageResponse = await fetch(downloadUrl);
      const blob = await imageResponse.blob();
      
      // Create object URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(`${graphicType.replace(/_/g, ' ')} graphic downloaded!`);
    } catch (error) {
      console.error('Graphic generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate graphic');
    } finally {
      setGeneratingGraphic(null);
    }
  };

  const triggerGraphic = async (graphicType) => {
    setLoading(true);
    try {
      await axios.post(`${API}/games/${gameId}/graphic?graphic_type=${graphicType}`, null, {
        headers: getAuthHeaders()
      });
      toast.success(graphicType === 'clear' ? 'Graphic cleared' : `${graphicType.replace('_', ' ')} triggered`);
      if (onGameUpdate) onGameUpdate();
    } catch (error) {
      toast.error('Failed to trigger graphic');
    } finally {
      setLoading(false);
    }
  };

  const addPenalty = async (team, scored) => {
    try {
      const playerName = penaltyPlayer[`team${team}`] || '';
      await axios.post(
        `${API}/games/${gameId}/penalty?team=${team}&scored=${scored}&player_name=${encodeURIComponent(playerName)}`,
        null,
        { headers: getAuthHeaders() }
      );
      toast.success(scored ? 'Goal recorded!' : 'Miss recorded');
      setPenaltyPlayer(prev => ({ ...prev, [`team${team}`]: '' }));
      if (onGameUpdate) onGameUpdate();
    } catch (error) {
      toast.error('Failed to record penalty');
    }
  };

  const removePenalty = async (team) => {
    try {
      await axios.delete(`${API}/games/${gameId}/penalty?team=${team}`, {
        headers: getAuthHeaders()
      });
      toast.success('Last penalty removed');
      if (onGameUpdate) onGameUpdate();
    } catch (error) {
      toast.error('Failed to remove penalty');
    }
  };

  const resetPenalties = async () => {
    try {
      await axios.post(`${API}/games/${gameId}/penalties/reset`, null, {
        headers: getAuthHeaders()
      });
      toast.success('Penalties reset');
      if (onGameUpdate) onGameUpdate();
    } catch (error) {
      toast.error('Failed to reset penalties');
    }
  };

  const triggerSubstitution = async () => {
    if (!substitution.playerOut || !substitution.playerIn) {
      toast.error('Please enter both player names');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/games/${gameId}/substitution`, {
        team: parseInt(substitution.team),
        player_out: substitution.playerOut,
        player_out_number: substitution.playerOutNumber,
        player_in: substitution.playerIn,
        player_in_number: substitution.playerInNumber
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Substitution graphic triggered');
      // Clear the form
      setSubstitution({
        team: substitution.team,
        playerOut: '',
        playerOutNumber: '',
        playerIn: '',
        playerInNumber: ''
      });
      if (onGameUpdate) onGameUpdate();
    } catch (error) {
      toast.error('Failed to trigger substitution');
    } finally {
      setLoading(false);
    }
  };

  const penalties1 = game.penalties_team1 || [];
  const penalties2 = game.penalties_team2 || [];
  const penScore1 = penalties1.filter(p => p.scored).length;
  const penScore2 = penalties2.filter(p => p.scored).length;

  const graphicsOverlayUrl = `${process.env.REACT_APP_BACKEND_URL}/api/overlay-graphics/${gameId}`;
  const stadiumScoreboardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/overlay-stadium/${gameId}`;

  return (
    <div className="space-y-4">
      {/* Graphics Overlay URL */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Graphics Overlay URL
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 font-body text-xs mb-2">
            Single URL for all graphics (intro, lineup, score bug, etc.)
          </p>
          <div className="bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
            {graphicsOverlayUrl}
          </div>
          <Button
            size="sm"
            onClick={() => copyToClipboard(graphicsOverlayUrl)}
            className="mt-2 w-full bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
          >
            Copy URL
          </Button>
        </CardContent>
      </Card>

      {/* Stadium Scoreboard URL */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Stadium Scoreboard (1216x514)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 font-body text-xs mb-2">
            Large display scoreboard with live clock, logos, scores, goal animations & cards
          </p>
          <div className="bg-[#0F172A] p-2 rounded font-mono text-xs text-[#D4E5ED] break-all">
            {stadiumScoreboardUrl}
          </div>
          <Button
            size="sm"
            onClick={() => copyToClipboard(stadiumScoreboardUrl)}
            className="mt-2 w-full bg-[#2A4A5C] hover:bg-[#3A5A6C] text-white font-body"
          >
            Copy URL
          </Button>
        </CardContent>
      </Card>

      {/* Graphics Controls */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm">Trigger Graphics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-slate-400 font-body text-xs">
            Active: <span className="text-white font-semibold">{game.active_graphic || 'None'}</span>
          </p>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => triggerGraphic('intro')}
              disabled={loading}
              className="h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-body"
            >
              <Users className="w-5 h-5 mr-2" />
              Team Intro
            </Button>
            
            <Button
              onClick={() => triggerGraphic('lineup')}
              disabled={loading}
              className="h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-body"
            >
              <Users className="w-5 h-5 mr-2" />
              Lineups
            </Button>
            
            <Button
              onClick={() => triggerGraphic('score_bug')}
              disabled={loading}
              className="h-14 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-body"
            >
              <Trophy className="w-5 h-5 mr-2" />
              Score Bug
            </Button>
            
            <Button
              onClick={() => triggerGraphic('period')}
              disabled={loading}
              className="h-14 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-body"
            >
              <Clock className="w-5 h-5 mr-2" />
              Period ({game.period?.replace('_', ' ').toUpperCase() || 'PRE'})
            </Button>
          </div>
          
          <Button
            onClick={() => triggerGraphic('clear')}
            disabled={loading}
            variant="outline"
            className="w-full h-10 border-slate-600 text-slate-300 hover:bg-slate-700 font-body"
          >
            <X className="w-4 h-4 mr-2" />
            Clear Graphics
          </Button>
          
          <p className="text-slate-500 font-body text-xs text-center">
            Lineup shows Team 1 for 15s → Team 2 for 15s → fades out
          </p>
        </CardContent>
      </Card>

      {/* Penalty Shootout */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Penalty Shootout
            </span>
            <span className="font-mono text-lg text-[#D4E5ED]">{penScore1} - {penScore2}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Team 1 Penalties */}
          <div className="bg-[#0F172A] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-body font-semibold">{game.team1.name}</span>
              <span className="font-mono text-xl text-green-400">{penScore1}</span>
            </div>
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={`t1-pen-slot-${i}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    penalties1[i]
                      ? penalties1[i].scored
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                      : 'border-2 border-slate-600'
                  }`}
                >
                  {penalties1[i] && (penalties1[i].scored ? '✓' : '✗')}
                </div>
              ))}
              {penalties1.length > 5 && penalties1.slice(5).map((p, i) => (
                <div
                  key={`t1-pen-extra-${i + 5}-${p.scored}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    p.scored ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}
                >
                  {p.scored ? '✓' : '✗'}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={penaltyPlayer.team1}
                onChange={(e) => setPenaltyPlayer(prev => ({ ...prev, team1: e.target.value }))}
                placeholder="Player name (optional)"
                className="flex-1 h-9 bg-slate-800 border-slate-600 text-white text-sm"
              />
              <Button
                size="sm"
                onClick={() => addPenalty(1, true)}
                className="bg-green-600 hover:bg-green-700 h-9 px-3"
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => addPenalty(1, false)}
                className="bg-red-600 hover:bg-red-700 h-9 px-3"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removePenalty(1)}
                className="text-slate-400 hover:text-white h-9 px-2"
              >
                <Minus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Team 2 Penalties */}
          <div className="bg-[#0F172A] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-body font-semibold">{game.team2.name}</span>
              <span className="font-mono text-xl text-green-400">{penScore2}</span>
            </div>
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={`t2-pen-slot-${i}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    penalties2[i]
                      ? penalties2[i].scored
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                      : 'border-2 border-slate-600'
                  }`}
                >
                  {penalties2[i] && (penalties2[i].scored ? '✓' : '✗')}
                </div>
              ))}
              {penalties2.length > 5 && penalties2.slice(5).map((p, i) => (
                <div
                  key={`t2-pen-extra-${i + 5}-${p.scored}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    p.scored ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}
                >
                  {p.scored ? '✓' : '✗'}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={penaltyPlayer.team2}
                onChange={(e) => setPenaltyPlayer(prev => ({ ...prev, team2: e.target.value }))}
                placeholder="Player name (optional)"
                className="flex-1 h-9 bg-slate-800 border-slate-600 text-white text-sm"
              />
              <Button
                size="sm"
                onClick={() => addPenalty(2, true)}
                className="bg-green-600 hover:bg-green-700 h-9 px-3"
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => addPenalty(2, false)}
                className="bg-red-600 hover:bg-red-700 h-9 px-3"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removePenalty(2)}
                className="text-slate-400 hover:text-white h-9 px-2"
              >
                <Minus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => triggerGraphic('penalties')}
              disabled={loading}
              className="flex-1 h-10 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-body"
            >
              <Target className="w-4 h-4 mr-2" />
              Show Penalties Graphic
            </Button>
            <Button
              onClick={resetPenalties}
              variant="outline"
              className="h-10 border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Substitution Graphic */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" />
            Substitution Graphic
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Team Selection */}
          <div>
            <Label className="text-slate-400 text-xs">Team</Label>
            <Select 
              value={substitution.team} 
              onValueChange={(v) => setSubstitution(prev => ({ ...prev, team: v }))}
            >
              <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-slate-600">
                <SelectItem value="1">{game.team1?.name || 'Team 1'}</SelectItem>
                <SelectItem value="2">{game.team2?.name || 'Team 2'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Player Out */}
          <div className="bg-red-900/20 rounded-lg p-3 space-y-2">
            <Label className="text-red-400 text-xs font-semibold">PLAYER OUT</Label>
            <div className="flex gap-2">
              <Input
                value={substitution.playerOut}
                onChange={(e) => setSubstitution(prev => ({ ...prev, playerOut: e.target.value }))}
                placeholder="Player name"
                className="flex-1 h-9 bg-slate-800 border-slate-600 text-white text-sm"
              />
              <Input
                value={substitution.playerOutNumber}
                onChange={(e) => setSubstitution(prev => ({ ...prev, playerOutNumber: e.target.value }))}
                placeholder="#"
                className="w-16 h-9 bg-slate-800 border-slate-600 text-white text-sm text-center"
              />
            </div>
          </div>

          {/* Player In */}
          <div className="bg-green-900/20 rounded-lg p-3 space-y-2">
            <Label className="text-green-400 text-xs font-semibold">PLAYER IN</Label>
            <div className="flex gap-2">
              <Input
                value={substitution.playerIn}
                onChange={(e) => setSubstitution(prev => ({ ...prev, playerIn: e.target.value }))}
                placeholder="Player name"
                className="flex-1 h-9 bg-slate-800 border-slate-600 text-white text-sm"
              />
              <Input
                value={substitution.playerInNumber}
                onChange={(e) => setSubstitution(prev => ({ ...prev, playerInNumber: e.target.value }))}
                placeholder="#"
                className="w-16 h-9 bg-slate-800 border-slate-600 text-white text-sm text-center"
              />
            </div>
          </div>

          <Button
            onClick={triggerSubstitution}
            disabled={loading || !substitution.playerOut || !substitution.playerIn}
            className="w-full h-10 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white font-body"
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Show Substitution
          </Button>
          
          <p className="text-slate-500 font-body text-xs text-center">
            Displays for 8 seconds then auto-clears
          </p>
        </CardContent>
      </Card>

      {/* Social Media Graphics Export */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-body text-sm flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Social Media Graphics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 font-body text-xs">
            Generate downloadable graphics for social media sharing. Requires team backgrounds to be uploaded in the Teams page.
          </p>

          {/* Team 1 Graphics */}
          <div className="bg-[#0F172A] rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-white font-body font-semibold text-sm">{game.team1?.name || 'Team 1'}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(1, 'lineup_post')}
                disabled={generatingGraphic === '1_lineup_post'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '1_lineup_post' ? 'Generating...' : 'Lineup Post'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(1, 'lineup_story')}
                disabled={generatingGraphic === '1_lineup_story'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '1_lineup_story' ? 'Generating...' : 'Lineup Story'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(1, 'matchday_post')}
                disabled={generatingGraphic === '1_matchday_post'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '1_matchday_post' ? 'Generating...' : 'Matchday Post'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(1, 'matchday_story')}
                disabled={generatingGraphic === '1_matchday_story'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '1_matchday_story' ? 'Generating...' : 'Matchday Story'}
              </Button>
              {game.active_goal?.team === 1 && (
                <Button
                  size="sm"
                  onClick={() => generateSocialGraphic(1, 'goal_story')}
                  disabled={generatingGraphic === '1_goal_story'}
                  className="h-9 bg-green-600 hover:bg-green-700 text-white text-xs col-span-2"
                >
                  <Target className="w-3 h-3 mr-1" />
                  {generatingGraphic === '1_goal_story' ? 'Generating...' : `⚽ Goal - ${game.active_goal?.scorer_name}`}
                </Button>
              )}
            </div>
          </div>

          {/* Team 2 Graphics */}
          <div className="bg-[#0F172A] rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-white font-body font-semibold text-sm">{game.team2?.name || 'Team 2'}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(2, 'lineup_post')}
                disabled={generatingGraphic === '2_lineup_post'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '2_lineup_post' ? 'Generating...' : 'Lineup Post'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(2, 'lineup_story')}
                disabled={generatingGraphic === '2_lineup_story'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '2_lineup_story' ? 'Generating...' : 'Lineup Story'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(2, 'matchday_post')}
                disabled={generatingGraphic === '2_matchday_post'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '2_matchday_post' ? 'Generating...' : 'Matchday Post'}
              </Button>
              <Button
                size="sm"
                onClick={() => generateSocialGraphic(2, 'matchday_story')}
                disabled={generatingGraphic === '2_matchday_story'}
                className="h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                <Image className="w-3 h-3 mr-1" />
                {generatingGraphic === '2_matchday_story' ? 'Generating...' : 'Matchday Story'}
              </Button>
              {game.active_goal?.team === 2 && (
                <Button
                  size="sm"
                  onClick={() => generateSocialGraphic(2, 'goal_story')}
                  disabled={generatingGraphic === '2_goal_story'}
                  className="h-9 bg-green-600 hover:bg-green-700 text-white text-xs col-span-2"
                >
                  <Target className="w-3 h-3 mr-1" />
                  {generatingGraphic === '2_goal_story' ? 'Generating...' : `⚽ Goal - ${game.active_goal?.scorer_name}`}
                </Button>
              )}
            </div>
          </div>

          <p className="text-slate-500 font-body text-xs text-center">
            Post = 1000x1000 (Instagram feed) • Story = 1080x1920 (Instagram/TikTok story)
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GraphicsPanel;
